import { Controller } from '@hotwired/stimulus'
import { format as formatTz } from 'date-fns-tz'
import parseISO from 'date-fns/parseISO'
import '../../../css/associated-person/associated-person.scss'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'
import escapeHtml from '../../utils/sanitize'
import {
  trans,
  STATUS_BANNED,
  STATUS_UNVERIFIED,
  DATE_OF_BIRTH_SHORT,
  DATE_OF_BIRTH_UNKNOWN,
  AGE_SHORT,
  AGE_RANGE,
  INCIDENT_COUNT,
  SEARCH_NO_RESULTS,
  SEARCH_HINT,
  LOADING_FAILED,
  REMOVE_FROM_ISSUE,
  UNLINK_FAILED_TOAST
} from '../../translator'

declare const accountDateFormat: string

interface ApIndicators {
  is_banned: boolean
  prior_incidents_count: number
  has_photo: boolean
}

interface ApPhoto {
  is_primary: boolean
  url: string
  thumbnail_url: string
}

interface ApTier3Fields {
  status: string
}

interface ApCriminalFlag {
  type: string
  label: string
  severity: string
}

interface ApAgeRange {
  from: number
  to: number
}

interface ApCard {
  id: string
  full_name: string
  date_of_birth: string | null
  date_of_birth_display: string | null
  age_range: ApAgeRange | null
  is_unverified: boolean
  indicators: ApIndicators
  criminal_history_flags: ApCriminalFlag[]
  photos?: ApPhoto[]
  tier: number
  tier_3_fields: ApTier3Fields | null
}

interface SearchResponse {
  results: ApCard[]
}

interface LinkResponse {
  associated_person: ApCard
}

/**
 * Drives the Associated Persons section on the Issue detail page: opens the search modal,
 * debounce-searches existing records via the session-authed internal endpoint, links the
 * selected record to the Issue and reflects the result in the section list without a full
 * page reload. Section/Add visibility is enforced from data-values so the controller stays
 * consistent with the server-side feature-flag and permission gating.
 */
export default class IssueApSectionController extends Controller {
  static targets = ['addTrigger', 'modal', 'searchInput', 'results', 'list', 'emptyState']
  static values = {
    searchUrl: String,
    linkUrl: String,
    createUrl: String,
    canCreate: { type: Boolean, default: false },
    enabled: { type: Boolean, default: false }
  }

  declare readonly addTriggerTarget: HTMLElement
  declare readonly hasAddTriggerTarget: boolean
  declare readonly modalTarget: HTMLElement
  declare readonly hasModalTarget: boolean
  declare readonly searchInputTarget: HTMLInputElement
  declare readonly hasSearchInputTarget: boolean
  declare readonly resultsTarget: HTMLElement
  declare readonly hasResultsTarget: boolean
  declare readonly listTarget: HTMLElement
  declare readonly hasListTarget: boolean
  declare readonly emptyStateTarget: HTMLElement
  declare readonly hasEmptyStateTarget: boolean
  declare readonly searchUrlValue: string
  declare readonly linkUrlValue: string
  declare readonly createUrlValue: string
  declare readonly canCreateValue: boolean
  declare readonly enabledValue: boolean

  private debounceTimer: ReturnType<typeof setTimeout> | null = null

  connect (): void {
    if (!this.enabledValue) {
      this.element.classList.add('d-none')

      return
    }

    if (!this.canCreateValue && this.hasAddTriggerTarget) {
      this.addTriggerTarget.classList.add('d-none')
    }
  }

  openModal (): void {
    if (!this.hasModalTarget) {
      return
    }

    this.modalTarget.classList.add('show')
    this.modalTarget.style.display = 'block'
    document.body.classList.add('modal-open')

    if (this.hasSearchInputTarget) {
      this.searchInputTarget.focus()
    }
  }

  closeModal (): void {
    if (!this.hasModalTarget) {
      return
    }

    this.modalTarget.classList.remove('show')
    this.modalTarget.style.display = 'none'
    document.body.classList.remove('modal-open')
  }

  onSearchInput (): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer)
    }

    this.debounceTimer = setTimeout(() => {
      // eslint-disable-next-line no-void
      void this.runSearch()
    }, 300)
  }

  private async runSearch (): Promise<void> {
    if (!this.hasResultsTarget || !this.hasSearchInputTarget) {
      return
    }

    const query = this.searchInputTarget.value.trim()

    if (query.length < 2) {
      this.resultsTarget.innerHTML = `<div class="text-muted rtext-sm py-2">${trans(SEARCH_HINT, {}, 'associated-persons')}</div>`

      return
    }

    try {
      const response = await fetchWithHandlingLoginRedirect(`${this.searchUrlValue}?query=${encodeURIComponent(query)}`)
      const data = await response.json() as SearchResponse

      if (data.results.length === 0) {
        this.resultsTarget.innerHTML = `<div class="text-muted rtext-sm py-2">${trans(SEARCH_NO_RESULTS, {}, 'associated-persons')}</div>`

        return
      }

      this.resultsTarget.innerHTML = data.results.map(ap => this.renderResultCard(ap)).join('')
    } catch {
      this.resultsTarget.innerHTML = `<div class="text-danger rtext-sm py-2">${trans(LOADING_FAILED, {}, 'associated-persons')}</div>`
    }
  }

  async select (event: Event): Promise<void> {
    const node = (event.target as Element).closest('[data-ap-result]')
    if (node === null) {
      return
    }

    const associatedPersonId = (node as HTMLElement).dataset.apId ?? ''
    if (associatedPersonId === '') {
      return
    }

    const data = new FormData()
    data.append('associated_person_id', associatedPersonId)

    try {
      const response = await fetchWithHandlingLoginRedirect(this.linkUrlValue, { method: 'POST', body: data })

      if (!response.ok) {
        return
      }

      const linked = await response.json() as LinkResponse
      this.addToList(linked.associated_person)
      this.closeModal()
      this.notifyHistoryUpdated()
    } catch {
      // Surface nothing destructive — the modal stays open so the user can retry.
    }
  }

  private addToList (ap: ApCard): void {
    if (!this.hasListTarget) {
      return
    }

    const alreadyListed = Array.from(this.listTarget.querySelectorAll('[data-ap-card]'))
      .some(card => (card as HTMLElement).dataset.apId === ap.id)
    if (alreadyListed) {
      return
    }

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add('d-none')
    }

    // List cards carry the remove control only when the viewer can manage links, and the
    // whole card navigates to the person via a stretched link.
    this.listTarget.insertAdjacentHTML('beforeend', this.renderCard(ap, this.canCreateValue, true))
  }

  /**
   * Unlinks a person from the Issue. Removes the card only on a successful response so a
   * failed request never leaves the user believing an association was dropped; on failure
   * the card stays and an error toast is surfaced.
   */
  async remove (event: Event): Promise<void> {
    const card = (event.target as Element).closest('[data-ap-card]')
    if (card === null) {
      return
    }

    const associatedPersonId = (card as HTMLElement).dataset.apId ?? ''
    if (associatedPersonId === '') {
      return
    }

    try {
      await fetchWithHandlingLoginRedirect(`${this.linkUrlValue}/${encodeURIComponent(associatedPersonId)}`, { method: 'DELETE' })
      card.remove()
      this.refreshEmptyState()
      this.notifyHistoryUpdated()
    } catch {
      this.notifyError()
    }
  }

  /**
   * Signal the Issue page to re-fetch its Recent Updates panel after a link/unlink, which the
   * backend records as an Issue history entry. Mirrors how record linking refreshes the panel.
   */
  private notifyHistoryUpdated (): void {
    document.dispatchEvent(new CustomEvent('HistoryUpdatedEvent'))
  }

  private refreshEmptyState (): void {
    if (!this.hasListTarget || !this.hasEmptyStateTarget) {
      return
    }

    const remaining = this.listTarget.querySelectorAll('[data-ap-card]').length
    this.emptyStateTarget.classList.toggle('d-none', remaining > 0)
  }

  private notifyError (): void {
    const jquery = (window as unknown as { $?: { notify?: (opts: object, settings: object) => void } }).$
    if (jquery !== undefined && typeof jquery.notify === 'function') {
      jquery.notify(
        { message: trans(UNLINK_FAILED_TOAST, {}, 'associated-persons') },
        { type: 'danger', placement: { from: 'bottom', align: 'right' } }
      )
    }
  }

  private renderResultCard (ap: ApCard): string {
    return `
      <div data-ap-result data-ap-id="${escapeHtml(ap.id)}" data-action="click->issue-ap-section#select" class="ap-section__result" role="button" tabindex="0">
        ${this.renderCard(ap)}
      </div>
    `
  }

  private renderCard (ap: ApCard, showRemove = false, navigable = false): string {
    // In the section list the whole card navigates to the person (Bootstrap stretched-link);
    // in the search-results modal the card is a select target, so the name stays a plain link.
    const nameClass = navigable ? 'ap-card__name font-weight-bold stretched-link' : 'ap-card__name font-weight-bold'

    return `
      <div class="ap-card card mb-2" data-ap-card data-ap-id="${escapeHtml(ap.id)}" data-tier="${escapeHtml(String(ap.tier))}">
        <div class="card-body d-flex align-items-start">
          ${this.renderPhoto(ap, navigable)}
          <div class="ap-card__body flex-grow-1">
            <a href="/associated-persons/${escapeHtml(ap.id)}" class="${nameClass}">${escapeHtml(ap.full_name)}</a>
            <div class="ap-card__dob text-muted rtext-sm">${this.renderDob(ap.date_of_birth, ap.date_of_birth_display, ap.age_range)}</div>
            <div class="ap-card__chips mt-1">${this.renderChips(ap)}</div>
          </div>
          ${showRemove ? this.renderRemoveButton() : ''}
        </div>
      </div>
    `
  }

  private renderRemoveButton (): string {
    return `
      <button type="button" class="ap-card__remove btn btn-link text-muted p-0 ml-2" data-action="issue-ap-section#remove" aria-label="${escapeHtml(trans(REMOVE_FROM_ISSUE, {}, 'associated-persons'))}">
        <i class="fas fa-times"></i>
      </button>
    `
  }

  private renderPhoto (ap: ApCard, expandable = false): string {
    const primary = (ap.photos ?? []).find(photo => photo.is_primary)
    if (primary === undefined) {
      return ''
    }

    // In the search-results modal the whole card is a select target, so the thumbnail stays a
    // plain image. In the section list it opens full-size via the shared lightGallery controller;
    // the anchor sits above the card's stretched link (see .ap-card__photo-gallery in the stylesheet).
    // Both branches size the image inline (56x56, object-fit cover, rounded) to match the
    // server-rendered card in _associated_person_card.html.twig, which does not rely on a
    // .ap-card__photo CSS rule (there is none).
    if (!expandable) {
      return `<img src="${escapeHtml(primary.thumbnail_url)}" alt="" class="ap-card__photo rounded mr-3" style="width:56px;height:56px;object-fit:cover;">`
    }

    return `
      <span class="ap-photo-gallery ap-card__photo-gallery mr-3" data-controller="associated-persons--photo-gallery">
        <a class="ap-photo__image rounded overflow-hidden d-block" href="${escapeHtml(primary.url)}" style="width:56px;height:56px;">
          <img src="${escapeHtml(primary.thumbnail_url)}" alt="" style="width:100%;height:100%;object-fit:cover;">
        </a>
      </span>
    `
  }

  private renderDob (dob: string | null, dobDisplay: string | null, ageRange: ApAgeRange | null): string {
    let dobText: string

    if (dob !== null) {
      const dateFormat = accountDateFormat.replace('mm', 'MM')
      dobText = `${trans(DATE_OF_BIRTH_SHORT, {}, 'associated-persons')} ${escapeHtml(formatTz(parseISO(dob), dateFormat))}`
    } else if (dobDisplay !== null) {
      dobText = `${trans(DATE_OF_BIRTH_SHORT, {}, 'associated-persons')} ${escapeHtml(dobDisplay)}`
    } else {
      dobText = trans(DATE_OF_BIRTH_UNKNOWN, {}, 'associated-persons')
    }

    return dobText + this.renderAgeRange(ageRange)
  }

  private renderAgeRange (ageRange: ApAgeRange | null): string {
    if (ageRange === null) {
      return ''
    }

    const age = ageRange.from === ageRange.to
      ? String(ageRange.from)
      : trans(AGE_RANGE, { '%from%': ageRange.from, '%to%': ageRange.to }, 'associated-persons')

    return ` · ${trans(AGE_SHORT, {}, 'associated-persons')} ${age}`
  }

  private renderChips (ap: ApCard): string {
    const chips: string[] = []

    if (ap.indicators.is_banned) {
      chips.push(`<span class="badge badge-danger">${trans(STATUS_BANNED, {}, 'associated-persons')}</span>`)
    }
    for (const flag of ap.criminal_history_flags ?? []) {
      chips.push(`<span class="badge badge-${escapeHtml(flag.severity)}">${escapeHtml(flag.label)}</span>`)
    }
    if (ap.is_unverified) {
      chips.push(`<span class="badge badge-warning">${trans(STATUS_UNVERIFIED, {}, 'associated-persons')}</span>`)
    }
    if (ap.indicators.prior_incidents_count > 0) {
      chips.push(`<span class="badge badge-secondary">${trans(INCIDENT_COUNT, { '%count%': ap.indicators.prior_incidents_count }, 'associated-persons')}</span>`)
    }

    // Join with a space so adjacent badges are separated, matching the whitespace the server
    // template renders between badge spans in _associated_person_card.html.twig.
    return chips.join(' ')
  }
}
